import { mapGetters } from 'vuex';
import { formatWordsAsSentences } from '../../utils/calculator/wordFormatter';
import SedanIcon from '../../components/Calculator/Icon/BodyTypes/SedanIcon';
import VanIcon from '../../components/Calculator/Icon/BodyTypes/VanIcon';
import TruckIcon from '../../components/Calculator/Icon/BodyTypes/TruckIcon';
import SUVIcon from '../../components/Calculator/Icon/BodyTypes/SuvIcon';

export default {
  data() {
    const bodyStyles = [
      { value: 'VAN', label: 'Van', icon: VanIcon },
      { value: 'SEDAN', label: 'Sedan', icon: SedanIcon },
      { value: 'SUV', label: 'SUV', icon: SUVIcon },
      { value: 'UTE', label: 'Ute', icon: TruckIcon }
    ];
    const cabTypes = [
      { value: 'EXTENDED', label: 'Extended' },
      { value: 'S/CAB', label: 'Single cab' },
      { value: 'D/CAB', label: 'Dual cab' }
    ];
    const vehicleCategories = [
      { value: ['SUV SMALL LUXURY', 'SMALL SUV'], label: 'Small SUV' },
      { value: ['SUV MEDIUM LUXURY', 'MEDIUM SUV'], label: 'Medium SUV' },
      { value: ['SUV LARGE LUXURY', 'LARGE SUV', 'RECREATIONAL SUV'], label: 'Large SUV' }
    ];
    const seats = [
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4', label: '4' },
      { value: '5', label: '5' },
      { value: '6', label: '6' },
      { value: '7', label: '7' },
      { value: '8', label: '8' },
      { value: '12', label: '12' },
      { value: '14', label: '14' },
      { value: '17', label: '17' }
    ];
    const fuelTypes = [
      { value: 'ELECTRIC', label: 'Electric' },
      { value: 'HYBRID', label: 'Hybrid' },
      { value: 'PETROL', label: 'Petrol' },
      { value: 'DIESEL', label: 'Diesel' }
    ];
    const price = [
      { value: '20000', label: '$20,000' },
      { value: '40000', label: '$40,000' },
      { value: '60000', label: '$60,000' },
      { value: '80000', label: '$80,000' },
      { value: '100000', label: '$100,000' },
      { value: '120000', label: '$100,000+' }
    ];
    const weeklyBudget = [
      { value: '0', label: '$0' },
      { value: '100', label: '$100' },
      { value: '200', label: '$200' },
      { value: '300', label: '$300' },
      { value: '400', label: '$400' },
      { value: '500', label: '$500' },
      { value: '600', label: '$600' },
      { value: '700', label: '$700' },
      { value: '800', label: '$800' },
      { value: '900', label: '$900' },
      { value: '1000', label: '$1000' }
    ];
    const driveTrain = [
      { value: '4WD', label: '4WD' },
      { value: 'RWD', label: 'RWD' },
      { value: 'FWD', label: 'FWD' }
    ];
    // Values must match formatKeyValueAsTrue / URL (uppercase keys on selectedTranmission)
    const tranmission = [
      { value: 'AUTOMATIC', label: 'Automatic' },
      { value: 'MANUAL', label: 'Manual' }
    ];
    return {
      cabTypes,
      fuelTypes,
      bodyStyles,
      vehicleCategories,
      price,
      seats,
      driveTrain,
      tranmission,
      weeklyBudget
    };
  },
  methods: {
    formatWordsAsSentences(value) {
      return formatWordsAsSentences(value);
    },
    filterFacetOptions(options = [], facetValues = [], matchKey = 'value') {
      if (!Array.isArray(options) || !options.length) return [];
      if (!Array.isArray(facetValues) || !facetValues.length) return [];

      const allowed = facetValues.map((v) => String(v).toLowerCase());

      return options?.filter((opt) => allowed.includes(String(opt[matchKey]).toLowerCase()));
    },
    filterVehicleCategoryOptions(options = [], facetValues = []) {
      if (!Array.isArray(options) || !options.length) return [];
      if (!Array.isArray(facetValues) || !facetValues.length) return [];

      const allowed = facetValues.map((v) => String(v).trim().toLowerCase());

      return options.filter((opt) => {
        const raw = opt?.value;

        // opt.value is array -> match any
        if (Array.isArray(raw)) {
          return raw.some((v) => allowed.includes(String(v).trim().toLowerCase()));
        }

        // fallback (if some option uses a single string)
        return allowed.includes(String(raw).trim().toLowerCase());
      });
    },
    formatOptions: function (values, labelTransform = (label) => label) {
      return values.map((value, index) => ({
        id: index + 1,
        ...value,
        label: labelTransform(values[index].label)
      }));
    }
  },
  computed: {
    ...mapGetters({
      vehicleSearchConfig: 'vehicleSearchConfig/getVehicleSearchConfig'
    }),
    fuelTypeOptions: function () {
      const filtered = this.filterFacetOptions(this.fuelTypes, this.vehicleSearchConfig?.fuelType);
      return this.formatOptions(filtered);
    },
    bodyStyleOptions: function () {
      const filtered = this.filterFacetOptions(this.bodyStyles, this.vehicleSearchConfig?.bodyType);
      return this.formatOptions(filtered);
    },
    // It will be used on ChooseVehiclePage
    bodyTypes: function () {
      const filtered = this.filterFacetOptions(this.bodyStyles, this.vehicleSearchConfig?.bodyType);
      return this.formatOptions(filtered, (label) => String(label).toUpperCase());
    },
    cabTypeOptions: function () {
      const filtered = this.filterFacetOptions(this.cabTypes, this.vehicleSearchConfig?.cabType);
      return this.formatOptions(filtered);
    },
    vehicleCategoryOptions: function () {
      const filtered = this.filterVehicleCategoryOptions(this.vehicleCategories, this.vehicleSearchConfig?.vehicleCategory);

      return this.formatOptions(filtered);
    },
    priceOptions: function () {
      return this.price;
    },
    weeklyOptions: function () {
      return this.weeklyBudget;
    },
    seatOptions: function () {
      const filtered = this.filterFacetOptions(this.seats, this.vehicleSearchConfig?.seats);
      return this.formatOptions(filtered);
    },
    driveTrainOptions: function () {
      const filtered = this.filterFacetOptions(this.driveTrain, this.vehicleSearchConfig?.drivetrain);
      return this.formatOptions(filtered);
    },
    tranmissionOptions: function () {
      const filtered = this.filterFacetOptions(this.tranmission, this.vehicleSearchConfig?.transmission);
      return this.formatOptions(filtered);
    },
    makesFromVehicleSearchConfig() {
      return this.vehicleSearchConfig?.make || [];
    }
  }
};
